/* In-memory IdentityClient for mock mode (auth off / no identity service).
 * Deterministically seeded and fully mutable, so the whole org/workspace flow
 * is exercisable offline: invites appear as pending members, role changes and
 * removals stick, rotating an API key swaps its value, revoking removes it.
 * Errors mirror the contract `{ error }` envelope. */

import type { IdentityClient } from "./IdentityClient";
import type { ApiError } from "../analytics";
import { DASHBOARD_NOW } from "../analytics";
import type {
  ApiKey,
  Invite,
  NewWorkspace,
  Organization,
  OrgMember,
  OrgRole,
  Workspace,
} from "./types";

function notFound(what: string): { error: ApiError } {
  return { error: { code: "not_found", message: `${what} not found` } };
}

function pseudoHex(seed: string, length: number): string {
  let h = 0x811c9dc5;
  const out: string[] = [];
  for (let i = 0; i < length; i += 1) {
    h = Math.imul(h ^ seed.charCodeAt(i % seed.length), 0x01000193) >>> 0;
    out.push(((h >>> (i % 24)) & 0xf).toString(16));
  }
  return out.join("");
}

const NOW = DASHBOARD_NOW.toISOString();

export const MOCK_ORG: Organization = { id: "org-ai21", name: "AI21" };

/** A seeded, acceptable invitation id so the accept flow is exercisable
 *  offline: GET /invite/{id} resolves to MOCK_ORG, POST .../accept consumes it.
 *  Any other id behaves like an unknown/consumed invite (not found). */
export const MOCK_INVITATION_ID = "00000000-0000-0000-0000-000000000000";

/** The mock "current user" — client.tsx's dev CurrentUser matches this entry
 *  so self-row logic (no remove/role-change on yourself) works offline. */
export const MOCK_SELF = {
  id: "3f9a1c7e-2b4d-4e8a-9c1f-6d5b0a2e8f13",
  name: "Dana Kessler",
  email: "dana.kessler@ai21.com",
};

function seedWorkspaces(): Workspace[] {
  return [
    {
      id: "ws-engineering",
      name: "Engineering",
      type: "private",
      color: "#a766fa",
      organizationId: MOCK_ORG.id,
      ownerId: MOCK_SELF.id,
    },
    {
      id: "ws-personal",
      name: "Personal",
      type: "personal",
      color: "#27b08b",
      organizationId: MOCK_ORG.id,
      ownerId: MOCK_SELF.id,
    },
  ];
}

function seedMembers(): OrgMember[] {
  return [
    {
      ...MOCK_SELF,
      role: "ADMIN",
      pending: false,
      addedOn: NOW,
    },
    {
      id: "user-amir",
      name: "Amir Navon",
      email: "amir.navon@ai21.com",
      role: "ADMIN",
      pending: false,
      addedOn: NOW,
    },
    {
      id: "user-maya",
      name: "Maya Sharon",
      email: "maya.sharon@ai21.com",
      role: "MEMBER",
      pending: false,
      addedOn: NOW,
    },
    {
      id: "user-invite-1",
      name: "",
      email: "noa.peretz@ai21.com",
      role: undefined,
      pending: true,
      addedOn: NOW,
    },
  ];
}

function seedKeys(): ApiKey[] {
  return [
    {
      id: "key-eng-ci",
      name: "CI pipeline",
      value: `tw-${pseudoHex("eng-ci", 32)}`,
      workspaceId: "ws-engineering",
      status: "ACTIVE",
    },
    {
      id: "key-eng-local",
      name: "Local development",
      value: `tw-${pseudoHex("eng-local", 32)}`,
      workspaceId: "ws-engineering",
      status: "ACTIVE",
      expiresAt: "2026-09-30",
    },
    {
      id: "key-personal",
      name: "API key",
      value: `tw-${pseudoHex("personal", 32)}`,
      workspaceId: "ws-personal",
      status: "ACTIVE",
    },
  ];
}

export interface MockIdentityClientOptions {
  delayMs?: number;
}

export class MockIdentityClient implements IdentityClient {
  private readonly delayMs: number;
  private workspaces: Workspace[];
  private members: OrgMember[];
  private keys: ApiKey[];
  // Invitation ids that still resolve/accept; the seeded one is consumed once
  // accepted, after which it reads as not found (like the real one-shot invite).
  private openInvites: Set<string>;
  private counter = 0;

  constructor(opts: MockIdentityClientOptions = {}) {
    this.delayMs = opts.delayMs ?? 180;
    this.workspaces = seedWorkspaces();
    this.members = seedMembers();
    this.keys = seedKeys();
    this.openInvites = new Set([MOCK_INVITATION_ID]);
  }

  private async delay(): Promise<void> {
    if (this.delayMs > 0) await new Promise((r) => setTimeout(r, this.delayMs));
  }

  async listWorkspaces(): Promise<Workspace[]> {
    await this.delay();
    return [...this.workspaces];
  }

  async createWorkspace(input: NewWorkspace): Promise<Workspace> {
    await this.delay();
    this.counter += 1;
    const workspace: Workspace = {
      id: `ws-new-${this.counter}`,
      name: input.name,
      type: input.type ?? "private",
      color: input.color,
      organizationId: MOCK_ORG.id,
      ownerId: MOCK_SELF.id,
    };
    this.workspaces = [...this.workspaces, workspace];
    return workspace;
  }

  async listApiKeys(workspaceId: string): Promise<ApiKey[]> {
    await this.delay();
    return this.keys.filter((k) => k.workspaceId === workspaceId);
  }

  async createApiKey(
    workspaceId: string,
    name: string,
    expiresAt?: string,
  ): Promise<ApiKey> {
    await this.delay();
    this.counter += 1;
    const key: ApiKey = {
      id: `key-new-${this.counter}`,
      name,
      value: `tw-${pseudoHex(`${workspaceId}:${name}:${this.counter}`, 32)}`,
      workspaceId,
      status: "ACTIVE",
      expiresAt,
    };
    this.keys = [...this.keys, key];
    return key;
  }

  async renameApiKey(keyId: string, name: string): Promise<void> {
    await this.delay();
    const key = this.keys.find((k) => k.id === keyId);
    if (!key) throw notFound(`API key '${keyId}'`);

    this.keys = this.keys.map((k) => (k.id === keyId ? { ...k, name } : k));
  }

  async rotateApiKey(keyId: string): Promise<ApiKey> {
    await this.delay();
    const key = this.keys.find((k) => k.id === keyId);
    if (!key) throw notFound(`API key '${keyId}'`);

    this.counter += 1;
    const rotated: ApiKey = {
      ...key,
      value: `tw-${pseudoHex(`${keyId}:rotated:${this.counter}`, 32)}`,
    };
    this.keys = this.keys.map((k) => (k.id === keyId ? rotated : k));
    return rotated;
  }

  async revokeApiKey(keyId: string): Promise<void> {
    await this.delay();
    if (!this.keys.some((k) => k.id === keyId)) {
      throw notFound(`API key '${keyId}'`);
    }

    this.keys = this.keys.filter((k) => k.id !== keyId);
  }

  async getOrganization(): Promise<Organization> {
    await this.delay();
    return MOCK_ORG;
  }

  async listOrgMembers(): Promise<OrgMember[]> {
    await this.delay();
    return [...this.members];
  }

  async inviteMember(email: string): Promise<void> {
    await this.delay();
    // Resending an existing invite is a no-op here.
    if (this.members.some((m) => m.email === email)) return;

    this.counter += 1;
    this.members = [
      ...this.members,
      {
        id: `user-invite-${this.counter + 1}`,
        name: "",
        email,
        role: undefined,
        pending: true,
        addedOn: NOW,
      },
    ];
  }

  async changeRole(email: string, role: OrgRole): Promise<void> {
    await this.delay();
    const member = this.members.find((m) => m.email === email);
    if (!member) throw notFound(`member '${email}'`);

    this.members = this.members.map((m) =>
      m.email === email ? { ...m, role } : m,
    );
  }

  async removeMember(_organizationId: string, userId: string): Promise<void> {
    await this.delay();
    if (!this.members.some((m) => m.id === userId)) {
      throw notFound(`member '${userId}'`);
    }

    this.members = this.members.filter((m) => m.id !== userId);
  }

  async getInvite(invitationId: string): Promise<Invite> {
    await this.delay();
    if (!this.openInvites.has(invitationId)) {
      throw notFound(`invitation '${invitationId}'`);
    }

    return { invitationId, organization: MOCK_ORG };
  }

  async acceptInvitation(invitationId: string): Promise<void> {
    await this.delay();
    if (!this.openInvites.has(invitationId)) {
      throw notFound(`invitation '${invitationId}'`);
    }

    // One-shot: consume the invite so a second accept reads as not found.
    this.openInvites.delete(invitationId);
  }
}
